import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColumn } from 'src/components/GridWrapper';
import PeriodHeadCell from 'src/components/custom-cells/PeriodHeadCell';
import useViewCheckbook from 'src/hooks/useViewCheckbook';
import LineItemInfo from 'src/sections/invoice_details/lineItemsColumns/LineItemInfo';
import { ActiveFilterCell, FixedFilterCell } from 'src/sections/view_checkbook/BooleanFilterCell';
import BooleanFilterCell from 'src/sections/view_checkbook/view_period_cell/BooleanFilterCell';
import DepartmentCell from 'src/sections/view_checkbook/view_period_cell/DepartmentCell';
import DescriptionCell from 'src/sections/view_checkbook/view_period_cell/DescriptionCell';
import GLCode from 'src/sections/view_checkbook/view_period_cell/GLCode';
import BudgetYearAmountCell from 'src/sections/view_checkbook/view_year_cell/BudgetYearAmountCell';
import CopyAcrossCell from 'src/sections/view_checkbook/view_year_cell/CopyAcrossCell';
import ForecastYearAmountCell from 'src/sections/view_checkbook/view_year_cell/ForecastYearAmountCell';
import { PeriodCell } from 'src/sections/view_checkbook/view_year_cell/PeriodCell';

export const useCheckbookYearColumns: (
  isEditMode: boolean,
  isColumnMenuDisable: boolean,
  periodLength: number
) => GridColumn<any>[] = (isEditMode, isColumnMenuDisable, periodLength = 12) => {
  const { t } = useTranslation();
  const { selectedYearDepartment, hasManageBudgetPermission, hasUsesInitialBudgetsPermission } =
    useViewCheckbook();

  const periods = useMemo(() => {
    let periods: GridColumn<any>[] = [];

    for (let i = 1; i <= periodLength; i++) {
      periods.push({
        field: `period${String(i).padStart(2, '0')}`,
        headerCell: PeriodHeadCell,
        dataType: 'dollar',
        component: PeriodCell,
        headerClassName: 'budget-amt-header',
        locked: false,
        sortable: false,
        width: 130,
        menu: false,
        show: true,
        childrenColumn: [
          ...(hasUsesInitialBudgetsPermission
            ? ([
                {
                  field: `initialBudgetPeriod${String(i).padStart(2, '0')}`,
                  title: 'Budget',
                  component: BudgetYearAmountCell,
                  headerClassName: 'budget-year-amt-header',
                  width: 110,
                  filter: 'numeric',
                  format: '{0:c2}',
                  filterFormat: 'c',
                  sortable: true,
                  locked: true,
                  show: true,
                  dataType: 'dollar',
                },
              ] as const)
            : []),

          {
            field: `budgetPeriod${String(i).padStart(2, '0')}`,
            title: 'Forecast',
            component: ForecastYearAmountCell,
            headerClassName: 'forecast-year-amt-header',
            width: hasUsesInitialBudgetsPermission ? 110 : 180,
            filter: 'numeric',
            format: '{0:c2}',
            filterFormat: 'c',
            sortable: true,
            locked: true,
            dataType: 'number',
          },
        ],
      });
    }

    return periods;
  }, [hasUsesInitialBudgetsPermission, periodLength]);

  const viewCheckbookColumns: GridColumn<any>[] = useMemo(
    () => [
      {
        field: 'selected',
        title: '',
        filter: 'boolean',
        menu: false,
        locked: true,
        width: 30,
        sortable: false,
        minResizableWidth: 30,
      },
      ...(isEditMode
        ? ([
            {
              field: 'errors',
              title: '',
              width: 40,
              component: LineItemInfo,
              menu: false,
              locked: true,
              sortable: false,
              minResizableWidth: 50,
            },
          ] as const)
        : []),

      {
        field: 'general_ledger',
        title: 'General Ledger',
        headerClassName: 'budget-amt-header',
        locked: true,
        sortable: false,
        menu: false,
        show: true,
        childrenColumn: [
          ...(selectedYearDepartment.departmentId === 0
            ? ([
                {
                  field: 'departments',
                  title: t('Departments'),
                  filter: 'text',
                  component: DepartmentCell,
                  menu: false,
                  headerCellTooltip: 'List of departments that use this GL code',
                  locked: true,
                  width: 80,
                  minResizableWidth: 80,
                  sortable: false,
                  show: true,
                },
              ] as const)
            : []),
          {
            field: 'code',
            title: t('Code'),
            filter: 'text',
            component: GLCode,
            locked: true,
            width: 120,
            minResizableWidth: 120,
            sortable: true,
          },

          {
            field: 'description',
            title: t('Description'),
            filter: 'text',
            component: DescriptionCell,
            locked: true,
            sortable: true,
            width: 250,
            minResizableWidth: 250,
            show: true,
          },
        ],
      },
      {
        field: 'glcode_type',
        title: 'GL Code Type',
        headerClassName: 'budget-amt-header',
        locked: true,
        sortable: false,
        menu: false,
        show: true,
        childrenColumn: [
          {
            field: 'isFixed',
            title: t('Fixed Code'),
            component: BooleanFilterCell,
            columnMenu: FixedFilterCell,
            locked: true,
            sortable: true,
            width: 120,
            minResizableWidth: 120,
            show: true,
          },
          {
            field: 'isActive',
            title: t('Active Code'),
            component: BooleanFilterCell,
            columnMenu: ActiveFilterCell,
            locked: true,
            sortable: true,
            width: 120,
            minResizableWidth: 120,
            show: true,
          },
        ]
      },

      ...(isEditMode && hasManageBudgetPermission
        ? ([
            {
              field: 'AcrossCell',
              title: 'Action',
              width: 140,
              component: CopyAcrossCell,
              menu: false,
              sortable: false,
              locked: true,
              minResizableWidth: 140,
              show: true,
            },
          ] as const)
        : []),
      ...periods,
    ],
    [hasManageBudgetPermission, isEditMode, periods, selectedYearDepartment?.departmentId, t]
  );

  const finalColumns = useMemo(
    () =>
      isColumnMenuDisable
        ? viewCheckbookColumns?.map((column) => ({ ...column, menu: false }))
        : viewCheckbookColumns,
    [isColumnMenuDisable, viewCheckbookColumns]
  );
  return finalColumns;
};
export default useCheckbookYearColumns;
